import { useLiveData, useService } from '@toeverything/infra';
import { useAtomValue } from 'jotai';
import { Suspense, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { PiqueErrorBoundary } from '../../../components/pique/pique-error-boundary';
import { appSidebarOpenAtom } from '../../../components/app-sidebar/index.jotai';
import { DesktopBackButton } from '../../../components/app-sidebar/sidebar-header/desktop-back-button';
import { SidebarSwitch } from '../../../components/app-sidebar/sidebar-header/sidebar-switch';
import { EdgelessViewToggle } from '../../../components/workspace-header/edgeless-view-toggle';
import {
  canUseEdgelessSplit,
  getWorkbenchSplitOrientation,
} from '../entities/workbench-layout-policy';
import { ViewService } from '../services/view';
import { WorkbenchService } from '../services/workbench';
import * as styles from './route-container.css';
import { useViewPosition } from './use-view-position';
import { ViewBodyTarget, ViewHeaderTarget } from './view-islands';

export interface Props {
  route: {
    Component: React.ComponentType;
  };
}

export const RouteContainer = () => {
  const viewPosition = useViewPosition();
  const leftSidebarOpen = useAtomValue(appSidebarOpenAtom);
  const view = useService(ViewService).view;
  const workbench = useService(WorkbenchService).workbench;
  const views = useLiveData(workbench.views$);
  const firstViewSize = useLiveData(views[0]?.size$ ?? null) ?? 100;

  const [isVertical, setIsVertical] = useState(
    () => getWorkbenchSplitOrientation(window.innerWidth) === 'vertical'
  );

  useEffect(() => {
    const handleResize = () => {
      setIsVertical(
        getWorkbenchSplitOrientation(window.innerWidth) === 'vertical'
      );
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isSecondViewMaximized =
    viewPosition.index === 1 && views.length > 1 && firstViewSize <= 5;

  const shouldShowControls = isVertical
    ? viewPosition.isLast
    : viewPosition.isFirst || isSecondViewMaximized;
  const supportsEdgelessSplit = canUseEdgelessSplit();

  return (
    <div className={styles.root}>
      <div className={styles.header} data-tauri-drag-region>
        {shouldShowControls && (
          <div className={styles.leftControls} data-tauri-drag-region>
            <SidebarSwitch show={!leftSidebarOpen} />
            {supportsEdgelessSplit && environment.isTauri && (
              <DesktopBackButton />
            )}
          </div>
        )}
        <ViewHeaderTarget
          viewId={view.id}
          className={styles.viewHeaderContainer}
          data-tauri-drag-region
        />
        {shouldShowControls && supportsEdgelessSplit && (
          <div className={styles.rightControls} data-tauri-drag-region>
            <EdgelessViewToggle />
          </div>
        )}
      </div>

      <PiqueErrorBoundary>
        <Suspense>
          <Outlet />
        </Suspense>
      </PiqueErrorBoundary>
      <ViewBodyTarget viewId={view.id} className={styles.viewBodyContainer} />
    </div>
  );
};
